<template>
  <div class="dashboard" id="profile"> 
    <div class="opciones">
      <div class="contenedor-opciones">
        <div class="opcion">
          <form @submit="downloadRecoveryKey">
            <h2>{{ $t('settings.recoveryTitle') }}</h2>
            <div style="display: none;">
              <qrcode-vue ref="recoveryKeyQr" :value="recoveryKeyLink" size="100" level="M"></qrcode-vue>
            </div>
            <p>{{ $t('settings.recoveryDescription') }}</p>
            <button class="button button-create" :title="$t('buttons.download')" type="submit">
              <download-cloud-icon size="1.5x" class="custom-class"></download-cloud-icon>
              <span>{{ $t('buttons.download') }}</span>
            </button>
          </form>
        </div>
        <div class="opcion">
          <form v-if="!user.lockPassword" @submit="updatePassword">
            <h2>{{ $t('settings.changePassword') }}</h2>
            <div class="inputs">
              <div :class="[isFocusPassword ? 'campo-activo' : 'campo']">
                <user-icon size="1.5x" class="custom-class"></user-icon>
                <password id="passwordInput" v-model="password" @score="changeScore" :placeholder="$t('settings.newPassword')" :badge="false" :showStrengthMeter="false" :toggle="true" :labelHide="$t('login.hidePassword')" :labelShow="$t('login.showPassword')" @focus="focusPasswordOn" @blur="focusPasswordOff"/>
              </div>
              
              <div class="scorebar">
                <div class="scorebar-value" :style="scoreBarStyles"></div>
              </div>
              
              <div :class="[isFocusPasswordConfirm ? 'campo-activo' : 'campo']">
                <user-icon size="1.5x" class="custom-class"></user-icon>
                <input class="input input--block" type="password" id="passwordConfirmInput" v-model="passwordConf" :placeholder="$t('settings.newPasswordConfirm')" autocomplete="new-password" @focus="focusPasswordConfirmOn" @blur="focusPasswordConfirmOff"/>
              </div>
            </div>
            <button class="button button-create" :title="$t('buttons.update')" type="submit">
              <span>{{ $t('buttons.update') }}</span>
            </button>
          </form>
        </div>
      </div>
      <div class="contenedor-opciones">
        <div class="opcion">
          <form @submit="updateSettings">
            <h2>{{ $t('settings.language') }}</h2>
            <div class="flex">
              <languages class="input input--block" :locale.sync="locale"></languages>
              <button class="button button-create" :title="$t('buttons.update')" type="submit">
                <span>{{ $t('buttons.update') }}</span>
              </button>
            </div>
          </form>
        </div>
        <div class="opcion">
          <form @submit="toggleMultiFactorAuth">
            <h2>{{ $t('settings.2fa') }}</h2>
            <div v-if="user.multiFactorAuth">
              <p>{{ $t('settings.2faisenabled') }}</p>
              <button class="button button-create" :title="$t('buttons.disable')" type="submit">
                <span>{{ $t('buttons.disable') }}</span>
              </button>
            </div>
            <div v-else>
              <p>{{ $t('settings.2faisdisabled') }}</p>
              <button class="button button-create" :title="$t('buttons.enable')" type="submit">
                <lock-icon size="1.5x" class="custom-class"></lock-icon>
                <span>{{ $t('buttons.enable') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
      <div class="contenedor-opciones">
        <div class="opcion" v-if="!isFreePlan">
          <div style="position: relative;">
            <div style="display: flex; align-items: center;">
              <h2 style="width: 265px;">{{ $t('settings.encryptionAlgorithm') }}</h2>
              <button style="font-size: 16px; background: none; color: black; padding: 0;" @click="showInfo" @mouseenter="mouseEnter" @mouseleave="mouseLeave">
                <info-icon size="1.5x" class="custom-class"></info-icon>
              </button>
            </div>
            <nav id="info-encryption-algorithm" :style="menuStyle">
              <div style="display: block; border-top: none; text-align: left;">
                <ul>
                  <li><strong>{{ $t('settings.AES256') }}</strong> {{ $t('settings.defaultSecure') }}</li>
                  <li><strong>{{ $t('settings.AES128') }}</strong> {{ $t('settings.faster') }}</li>
                </ul>
              </div>
            </nav>
          </div>
          <div class="flex">
            <select class="input input--block" v-model="encryptionAlgorithm">
              <option :value=0>{{ $t('settings.AES256') }}</option>
              <option :value=2>{{ $t('settings.AES128') }}</option>
            </select>
            <button class="button button-create" :title="$t('buttons.update')" @click="updateEncryptionAlgorithm">
              <span>{{ $t('buttons.update') }}</span>
            </button>
          </div>
        </div>
        <div class="opcion" v-if="isAdmin">
          <h2>{{ $t('buttons.deleteAccount') }}</h2>
          <p>{{ $t('settings.deleteAccountText') }}</p>
          <button @click="deleteAccount" class="button button-create delete" :title="$t('buttons.deleteAccount')">
            <trash-2-icon size="1.5x" class="custom-class"></trash-2-icon>
            <span>{{ $t('buttons.deleteAccount') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { users as api, files as ibbapi } from '@/api';
import * as pdf from '@/api/pdf';
import Languages from '@/components/settings/Languages.vue';
import QrcodeVue from 'qrcode.vue';
import { DownloadCloudIcon, UserIcon, LockIcon, InfoIcon, Trash2Icon } from 'vue-feather-icons';
import Password from 'vue-password-strength-meter';

export default {
  name: 'settings',
  components: {
    Languages,
    QrcodeVue,
    DownloadCloudIcon,
    UserIcon,
    Password,
    LockIcon,
    InfoIcon,
    Trash2Icon
  },
  data: function () {
    return {
      password: '',
      passwordConf: '',
      locale: '',
      recoveryKey: '',
      recoveryKeyLink: '',
      isFocusPassword: false,
      isFocusPasswordConfirm: false,
      passwordScore: -1,
      encryptionAlgorithm: '',
      isShow: false,
      isButton: false,
      isFreePlan: true
    };
  },
  computed: {
    ...mapState([ 'user', 'accountInfo', 'userInfo' ]),
    ...mapGetters(['isAdmin']),
    passwordClass () {
      const baseClass = 'input input--block';

      if (this.password === '' && this.passwordConf === '') {
        return baseClass;
      }

      if (this.password === this.passwordConf) {
        return `${baseClass} input--green`;
      }

      return `${baseClass} input--red`;
    },
    scoreBarStyles() {
      let widthBar = ((this.passwordScore+1)/5)*100 + '%';
      return {width: widthBar};
    },
    menuStyle () {
      if (this.isShow) {
        return {display: 'block'};
      } else {
        return {display: 'none'};
      }
    },
  },
  async created () {
    this.locale = this.user.locale;
    try {
      let invoicing = await ibbapi.getInvoicing();
      if (invoicing.subscription.amount === 0) {
        this.isFreePlan = true;
      } else {
        this.isFreePlan = false;
      }
    } catch (e) {
      this.$showError(e);
    }
    this.fetchData();
  },
  mounted () {
    let thisComponent = this;
    let JavaEventListenerClass = Module.EventListener.extend('EventListener', {
      onEvent: function(event)
      {
        if (event.type == Module.EventType.ACCOUNT_INFO.value)
        {
          thisComponent.fetchData();
        }

        return;
      }
    });

    this.interfaceObject = new JavaEventListenerClass();
    ibb.addEventListener(this.interfaceObject);
    window.addEventListener('click', this.click);
  },
  beforeDestroy () {
    ibb.removeEventListener(this.interfaceObject);
    window.removeEventListener('click', this.click);
  },
  methods: {
    ...mapMutations([ 'updateUser' ]),
    async fetchData () {
      try {
        let action = await ibbapi.getAccountInfo();
        this.encryptionAlgorithm = action.accountInfo.crypto;
      } catch (e) {
        this.$showError(e);
      }
    },
    focusPasswordOn () {
      this.isFocusPassword = true;
    },
    focusPasswordOff () {
      this.isFocusPassword = false;
    },
    focusPasswordConfirmOn () {
      this.isFocusPasswordConfirm = true;
    },
    focusPasswordConfirmOff () {
      this.isFocusPasswordConfirm = false;
    },
    changeScore (score) {
      this.passwordScore = score;
    },
    async updatePassword (event) {
      event.preventDefault();

      if (this.password !== this.passwordConf || this.password === '') {
        return;
      }

      let strongPassword = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})');
      if (!strongPassword.test(this.password)) {
        this.$showError(this.$t('login.incorrectPassword'));
        return;
      }

      try {
        const data = { id: this.user.id, password: this.password };
        await api.update(data, ['password']);
        this.updateUser(data);
        this.$showSuccess(this.$t('settings.passwordUpdated'));
      } catch (e) {
        this.$showError(e);
      }
    },
    async toggleMultiFactorAuth (event) {
      event.preventDefault();

      this.$store.commit('showHover', 'mfa');
    },
    async updateSettings (event) {
      event.preventDefault();

      try {
        const data = { id: this.user.id, locale: this.locale };
        await api.update(data, ['locale']);
        this.updateUser(data);
        this.$showSuccess(this.$t('settings.settingsUpdated'));
      } catch (e) {
        this.$showError(e);
      }
    },
    async updateEncryptionAlgorithm(event) {
      event.preventDefault();
      let action = await ibb.setAccountCryptoScheme(this.encryptionAlgorithm);
      if (!action.error.type) {
        this.$showSuccess(this.$t('settings.algorithmChanged'));
      }
    },
    mouseEnter() {
      this.isButton = true;
    },
    mouseLeave() {
      this.isButton = false;
    },
    showInfo() {
      this.isShow = !this.isShow;
    },
    click() {
      if (!this.isButton && this.isShow) {
        this.isShow = !this.isShow;
      }
    },
    deleteAccount() {
      this.$store.commit('showHover', 'cancel-subscription');
    },
    async downloadRecoveryKey (event) {
      event.preventDefault();

      try {
        this.recoveryKey = await ibbapi.getRecoveryKey();
        pdf.generateRecoveryKeyPdf.call(this, this.accountInfo, this.userInfo, this.recoveryKey);
      } catch (e) {
        this.$showError(e);
      }
    } 
  }
};
</script>
